'use client';

import { FilePathUtils } from '@/config';
import { Link, usePathname } from '@/i18n/routing';
import { Drawer, DrawerContent, DrawerTrigger } from '@/ui/drawer/drawer';
import { fallbackImages } from '@/utils/fallback-images';
import { useHeaderNavLinks } from '@/utils/translations-utils';
import { VashaUtils } from '@/utils/vasha-utils';
import { CustomerViewModel } from '@hidayahlab/academy-suite-corelib';
import { Menu02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { Button, cn } from '@tajdid/tajdid-ui';
import { XCloseIcon } from '@tajdid/tajdid-ui/icons';
import { User } from 'lucia';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import { Logo } from '../logo';
import BottomNav from './bottom-nav';
import { LanguageToggle } from './language-toggler';

type NavbarDetailsProps = {
  user?: User | null;
  handleLogout: () => void;
  customer?: CustomerViewModel;
};

export const hideNavbar = [''];

export const isResourcePage = (pathName: string) => {
  const pattern = /^\/my-curriculum\/[^\/]+\/resources\/.*/;

  return pattern.test(pathName);
};

export default function NavbarDetails({ user, customer }: NavbarDetailsProps) {
  const t = useTranslations();
  const pathName = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const headerNavLinks = useHeaderNavLinks();
  const mobileNavLinks = [
    {
      title: t('navigation.browse'),
      href: '/courses',
      active: pathName === '/browse',
    },
    {
      title: t('navigation.myClasses'),
      href: user ? '/my-classes' : '/login?redirect=/my-classes',
      active: pathName === '/my-classes',
    },
    {
      title: t('navigation.myWebinars'),
      href: user ? '/webinars' : '/login?redirect=/webinars',
      active: pathName === '/webinars',
    },
  ];
  const isVashaAcademy =
    !!customer?.code && VashaUtils.isVashaAcademy(customer.code);

  if (pathName.startsWith('/ielts-mock')) {
    return null;
  }

  const hrefForItem = (slug: string) =>
    slug === '/my-classes'
      ? user
        ? '/my-classes'
        : '/login?redirect=/my-classes'
      : slug;

  return (
    <>
      {!hideNavbar.includes(pathName) && (
        <nav className="sticky top-0 z-50 self-start isolate w-full bg-white/80 px-2 py-2 backdrop-blur-md lg:py-3">
          <div className="container max-w-6xl">
            <div className="grid min-h-[60px] grid-cols-[auto_1fr_auto] items-center gap-3 rounded-[32px] border border-gray-200 bg-white px-4 py-2 shadow-sm lg:min-h-[64px] lg:grid-cols-[1fr_auto_1fr] lg:gap-4 lg:px-6 lg:py-2.5">
              <Link href="/" className="relative min-w-0 shrink">
                <Logo
                  src={
                    (customer?.logoColor &&
                      `${FilePathUtils.customerLogoPath(customer.id, 'color')}/${customer.logoColor}`) ??
                    ''
                  }
                  variant="color"
                  width={170}
                  height={25}
                  alt={customer?.businessName}
                />
              </Link>

              <nav className="hidden items-center justify-center lg:flex">
                <div className="flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-gray-200 bg-gray-50 p-1">
                  {headerNavLinks.map(item => (
                    <Link
                      className="whitespace-nowrap"
                      key={item.slug}
                      href={hrefForItem(item.slug)}
                    >
                      <span
                        className={cn(
                          'block rounded-full px-4 py-1.5 text-base font-medium transition-all duration-200',
                          item.slug === pathName
                            ? 'bg-white text-primary shadow-sm ring-1 ring-gray-200/80'
                            : 'text-gray-500 hover:text-black',
                        )}
                      >
                        {item.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </nav>

              <div className="flex items-center justify-end gap-2">
                <div className="hidden items-center gap-2 lg:flex xl:gap-3">
                  {isVashaAcademy && <LanguageToggle />}
                  {!user ? (
                    <Link href="/login">
                      <Button className="rounded-full">
                        {t('button.signIn')}
                      </Button>
                    </Link>
                  ) : (
                    <Link className="flex items-center gap-2" href="/profile">
                      <Image
                        className="h-8 w-8 rounded-full"
                        height={32}
                        width={32}
                        alt="profile"
                        src={
                          user.picture
                            ? `${FilePathUtils.userProfilePath(user.id)}/${user.picture}`
                            : fallbackImages.user
                        }
                      />
                      <p className="hidden text-sm font-semibold text-gray-700 xl:block">
                        {user.name ?? 'Anonymous'}
                      </p>
                    </Link>
                  )}
                </div>

                <div
                  className={cn(
                    'flex items-center gap-2 lg:hidden',
                    isResourcePage(pathName) && 'hidden',
                  )}
                >
                  {isVashaAcademy && <LanguageToggle />}
                  <Drawer
                    direction="left"
                    open={isOpen}
                    onOpenChange={setIsOpen}
                  >
                    <DrawerTrigger>
                      <HugeiconsIcon icon={Menu02Icon} />
                    </DrawerTrigger>
                    <DrawerContent>
                      <div className="relative mobile-menu">
                        <div className="absolute right-3 top-3 cursor-pointer p-4">
                          <XCloseIcon onClick={() => setIsOpen(false)} />
                        </div>
                        <div className="p-4">
                          <div className="mb-6">
                            <Logo
                              src={
                                (customer?.logoColor &&
                                  `${FilePathUtils.customerLogoPath(customer.id, 'color')}/${customer.logoColor}`) ??
                                ''
                              }
                              variant="color"
                              width={175}
                              height={34}
                              alt={customer?.businessName}
                            />
                          </div>
                          <div className="space-y-3">
                            {mobileNavLinks.map(item => (
                              <Link
                                key={item.href}
                                href={item.href}
                                className={cn(
                                  'mb-3 flex items-center gap-2 border-b border-gray-100 pb-3 text-base',
                                  item.active
                                    ? 'font-semibold text-primary'
                                    : 'text-gray-600',
                                )}
                                onClick={() => setIsOpen(false)}
                              >
                                {item.title}
                              </Link>
                            ))}
                          </div>
                          {!user && (
                            <Link
                              href="/login"
                              onClick={() => setIsOpen(false)}
                            >
                              <Button fullWidth className="mt-6 rounded-full">
                                {t('button.signIn')}
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </DrawerContent>
                  </Drawer>
                </div>
              </div>
            </div>
          </div>
        </nav>
      )}
      <div className="h-2" />
      <BottomNav />
    </>
  );
}
